import { useMemo } from "react";
import { Card, CardContent, CardTitle } from "@/core/components/ui/card";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetBusinessTechnicianJobCounts } from "@/business/query";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { createDefaults } from "@/core/utils/loading";
import { useGetUsersWithStats } from "@/user/query";
import { DetailedUserWithStatsAndJobCount } from "@/user/user";
import { isAdmin } from "@/user/utils";
import { InfoPair } from "./info-pair";
import { LegendLabel } from "./legend-label";
import { ParticipationTrackerListItem } from "./participation-tracker-list-item";
import { ParticipationTrackerListItemSkeleton } from "./participation-tracker-list-item-skeleton";

const LOADING_USERS = createDefaults("loading-user", 5);

interface ParticipationTrackerListContentProps {
  isLoading: boolean;
  sortedUsers: DetailedUserWithStatsAndJobCount[];
  maxValue: number;
}

const ParticipationTrackerListContent = ({
  isLoading,
  sortedUsers,
  maxValue,
}: ParticipationTrackerListContentProps) => {
  if (isLoading) {
    return (
      <Stack gap="lg">
        {LOADING_USERS.map((user, index) => (
          <ParticipationTrackerListItemSkeleton
            key={user.id}
            widthPercentage={100 - index * 20}
          />
        ))}
      </Stack>
    );
  }
  return (
    <Stack gap="lg">
      {sortedUsers.map((user) => (
        <ParticipationTrackerListItem
          key={user.id}
          user={user}
          maxValue={maxValue}
        />
      ))}
    </Stack>
  );
};

const ParticipationTrackerPane = () => {
  const { businessId, selectedBusiness } = useBusiness();
  const { enabled: showCompletedJobsOnDashboard } = useFeatureFlag(
    FEATURE_FLAG_NAMES.show_completed_jobs_on_dashboard,
  );
  const serviceTitanEnabled = selectedBusiness?.service_titan_enabled;

  const showCompletedJobs = showCompletedJobsOnDashboard && serviceTitanEnabled;

  const {
    data: technicianJobCounts = {},
    isLoading: isLoadingTechnicianJobCounts,
  } = useGetBusinessTechnicianJobCounts(businessId, {
    enabled: showCompletedJobs,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const { data: users = [], isLoading } = useGetUsersWithStats(
    businessId,
    "last_thirty_days",
  );

  const usersWithoutAdminsWithCounts = useMemo(
    () =>
      users
        .filter((user) => !isAdmin(user.role))
        .map((user) => ({
          ...user,
          job_count: technicianJobCounts[user.id]?.job_count ?? 0,
        })),
    [users, technicianJobCounts],
  );

  const sortedUsers = useMemo(
    () =>
      [...usersWithoutAdminsWithCounts].sort(
        (a, b) => (b.stats?.signup_count ?? 0) - (a.stats?.signup_count ?? 0),
      ),
    [usersWithoutAdminsWithCounts],
  );

  const totalJobsCompleted = useMemo(
    () => sortedUsers.reduce((acc, user) => acc + (user.job_count ?? 0), 0),
    [sortedUsers],
  );

  const maxValue = useMemo(() => {
    if (sortedUsers.length === 0) return 0;

    const maxSignups = Math.max(
      ...sortedUsers.map((user) => user.stats?.signup_count ?? 0),
    );
    const maxJobsCompleted = Math.max(
      ...sortedUsers.map((user) => user.job_count ?? 0),
    );

    return Math.max(maxSignups, maxJobsCompleted);
  }, [sortedUsers]);

  const totalSignups = useMemo(
    () =>
      sortedUsers.reduce(
        (acc, user) => acc + (user.stats?.signup_count ?? 0),
        0,
      ),
    [sortedUsers],
  );

  return (
    <Card className="w-[448px] min-w-[448px] rounded-2xl">
      <CardContent className="flex flex-col gap-6 p-4">
        <CardTitle className="flex w-full flex-col gap-1">
          Participation Tracker
          <Typography variant="subtitleW3" textColor="tertiary">
            Last 30 days
          </Typography>
        </CardTitle>

        <Stack direction="row" align="center" gap="lg">
          <InfoPair
            label="Total sign-ups"
            value={totalSignups.toString()}
            isLoading={isLoading}
          />
          {showCompletedJobs && (
            <InfoPair
              label="Total Jobs"
              value={totalJobsCompleted.toString()}
              isLoading={isLoading || isLoadingTechnicianJobCounts}
            />
          )}
          <InfoPair
            label="Team Size"
            value={sortedUsers.length.toString()}
            isLoading={isLoading}
          />
        </Stack>
        <Stack direction="row" justify="end" align="center" gap="lg" fullWidth>
          <LegendLabel label="Sign-ups" type="signups" />
          {showCompletedJobs && (
            <LegendLabel label="Jobs Completed" type="jobs_completed" />
          )}
        </Stack>
        <ScrollArea>
          <ParticipationTrackerListContent
            isLoading={isLoading}
            sortedUsers={sortedUsers}
            maxValue={maxValue}
          />
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default ParticipationTrackerPane;
